import React, { useState, useEffect, useCallback } from "react";
import {
  Box,
  Typography,
  Paper,
  Button,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { useParams, useNavigate } from "react-router-dom";
import { useIntl } from "react-intl";
import { useNotification } from "../../providers/NotificationProvider";
import { fetchDepartment, fetchActionsByDepartment } from "../../api/memberApi";

function DetailRow({ label, value }) {
  return (
    <Box
      sx={{
        display: "flex",
        py: 1.5,
        borderBottom: "1px solid",
        borderColor: "divider",
      }}
    >
      <Typography
        variant="body2"
        sx={{ width: 200, flexShrink: 0, color: "text.secondary", fontWeight: 500 }}
      >
        {label}
      </Typography>
      <Typography variant="body2">{value || "—"}</Typography>
    </Box>
  );
}

export default function DepartmentDetailPage() {
  const { businessDomainId, businessUnitId, departmentId } = useParams();
  const navigate = useNavigate();
  const intl = useIntl();
  const notify = useNotification();
  const t = (id) => intl.formatMessage({ id });
  const [item, setItem] = useState(null);
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const [dept, acts] = await Promise.all([
        fetchDepartment(departmentId),
        fetchActionsByDepartment(departmentId),
      ]);
      setItem(dept);
      setActions(Array.isArray(acts) ? acts : []);
    } catch (e) {
      notify.apiError(e);
    } finally {
      setLoading(false);
    }
  }, [departmentId]); // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadData();
  }, [loadData]);

  if (loading)
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 6 }}>
        <CircularProgress />
      </Box>
    );
  if (!item) return null;

  return (
    <Box>
      <Box sx={{ display: "flex", alignItems: "center", mb: 3, gap: 2 }}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() =>
            navigate(
              `/strategy/${businessDomainId}/business-units/${businessUnitId}/departments`
            )
          }
          variant="text"
        >
          {t("common.back")}
        </Button>
        <Typography variant="h5" sx={{ fontWeight: 600 }}>
          {t("department.detail.title")}
        </Typography>
      </Box>

      <Paper variant="outlined" sx={{ p: 3, mb: 3 }}>
        <DetailRow label={t("department.name")} value={item.departmentName} />
        <DetailRow label={t("department.job")} value={item.jobName} />
        {item.departmentDescription !== undefined && (
          <DetailRow label={t("department.description")} value={item.departmentDescription} />
        )}
      </Paper>

      <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
        {t("action.list.title")}
      </Typography>
      {actions.length === 0 ? (
        <Typography color="text.secondary">{t("common.noData")}</Typography>
      ) : (
        <TableContainer component={Paper} variant="outlined">
          <Table>
            <TableHead>
              <TableRow sx={{ backgroundColor: "grey.50" }}>
                <TableCell sx={{ fontWeight: 600 }}>{t("action.name")}</TableCell>
                <TableCell sx={{ fontWeight: 600 }}>{t("action.description")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {actions.map((action, idx) => (
                <TableRow key={action.actionId || idx}>
                  <TableCell>{action.actionName || "—"}</TableCell>
                  <TableCell>{action.actionDescription || "—"}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
}
